import type { RiskComponentProps } from "./RiskComponent.type";
import type { GridSampleItem } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/gridUtils";
import type { Risk } from "@/entities/risk-matrix";

import { Form, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { SeverityFieldNames } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/constants";
import { SeverityGridPreview } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/gridUtils";
import { useRiskMatrixFromIntervention } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/hooks/useRiskMatrixFromIntervention";
import { useIsFormEnginePreviewMode } from "@/components/FormEngine/hooks/useIsFormEnginePreviewMode";
import { useFormWatchField } from "@/modules/form";
import { riskDisplayLabel } from "@/modules/risk";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useFormInstance } = Form;

const RiskComponent = ({ ...selectProps }: RiskComponentProps) => {
  const { t } = useTranslation();
  const { riskMatrix } = useRiskMatrixFromIntervention();
  const isPreviewMode = useIsFormEnginePreviewMode();

  const form = useFormInstance();
  const probability = useFormWatchField(SeverityFieldNames.probability);
  const consequence = useFormWatchField(SeverityFieldNames.consequence);

  const risks: Risk[] | GridSampleItem[] = isPreviewMode
    ? SeverityGridPreview.buildSampleRisks()
    : (riskMatrix?.risks ?? []).map((risk) => ({
        ...risk,
        consequence: risk.consequence ?? 0,
        probability: risk.probability ?? 0,
        id: risk.id ?? 0,
        weight: risk.weight ?? 0,
      }));

  const selectedRisk = risks.find(
    (risk): risk is Risk =>
      risk &&
      "consequence" in risk &&
      risk.consequence === consequence &&
      "probability" in risk &&
      risk.probability === probability
  );

  useEffect(() => {
    const value = selectedRisk?.value ?? null;
    form.setFieldValue(SeverityFieldNames.risk, value);
  }, [selectedRisk?.value, form]);

  return (
    <Select
      {...selectProps}
      placeholder={t("form:staticField.risk.placeholder")}
      disabled={!isPreviewMode}
      value={selectedRisk?.value}
    >
      <Option key={selectedRisk?.id} value={selectedRisk?.value}>
        {riskDisplayLabel(selectedRisk)}
      </Option>
    </Select>
  );
};

export default RiskComponent;
